import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Search, Image, Check } from 'lucide-react';
import { useAppStore } from '../../stores/appStore';
import { Modal } from '../ui/Modal/Modal';
import { Button } from '../ui/Button/Button';
import { Input } from '../ui/Input/Input';

export interface GalleryPickerResult {
  id: string;
  url: string;
  title: string;
}

interface GalleryPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (result: GalleryPickerResult) => void;
}

export function GalleryPickerModal({ isOpen, onClose, onSelect }: GalleryPickerModalProps) {
  const items = useAppStore((s) => s.galleryImages);
  const galleryHasMore = useAppStore((s) => s.galleryHasMore);
  const galleryLoadingMore = useAppStore((s) => s.galleryLoadingMore);
  const refreshGallery = useAppStore((s) => s.refreshGallery);
  const loadMoreGallery = useAppStore((s) => s.loadMoreGallery);

  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  // Refresh gallery when modal opens
  useEffect(() => {
    if (isOpen) {
      refreshGallery();
      setSelectedId(null);
      setQuery('');
    }
  }, [isOpen, refreshGallery]);

  // Infinite scroll observer
  useEffect(() => {
    if (!isOpen || !loadMoreTriggerRef.current || items.length === 0) return;
    const timeoutId = setTimeout(() => {
      if (!loadMoreTriggerRef.current) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && galleryHasMore && !galleryLoadingMore && !query) {
            loadMoreGallery();
          }
        },
        { root: scrollContainerRef.current, rootMargin: '100px', threshold: 0 }
      );
      observer.observe(loadMoreTriggerRef.current);
      return () => observer.disconnect();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [isOpen, items.length, galleryHasMore, galleryLoadingMore, loadMoreGallery, query]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(i => (i.title || i.filename || '').toLowerCase().includes(q));
  }, [items, query]);

  const handleInsert = () => {
    const selected = items.find(i => i.id === selectedId);
    if (selected) {
      onSelect({
        id: selected.id,
        url: selected.url,
        title: selected.title || selected.filename,
      });
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select from Gallery"
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleInsert} disabled={!selectedId}>
            Insert Selected
          </Button>
        </>
      }
    >
      <div className="flex flex-col">
        {/* Search */}
        <div className="relative mb-4 flex-shrink-0">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <Input
            type="text"
            placeholder="Search images..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Grid - explicit max height for scrolling */}
        <div className="overflow-auto max-h-[60vh]" ref={scrollContainerRef}>
          {filtered.length === 0 ? (
            <div className="flex items-center justify-center h-full min-h-[300px]">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100">
                  <Image className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">
                  {query ? 'No images found' : 'No images in gallery yet'}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {filtered.map((img) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setSelectedId(img.id)}
                    className={`aspect-square bg-gray-100 rounded-lg overflow-hidden relative group transition-all ${
                      selectedId === img.id
                        ? 'ring-2 ring-primary-500 ring-offset-2'
                        : 'hover:ring-2 hover:ring-gray-300'
                    }`}
                  >
                    <img
                      src={img.thumbnail || img.url}
                      alt={img.title || img.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {selectedId === img.id && (
                      <div className="absolute inset-0 bg-primary-500/20 flex items-center justify-center">
                        <div className="bg-primary-500 rounded-full p-1">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-xs text-white truncate">{img.title || img.filename}</p>
                    </div>
                  </button>
                ))}
              </div>
              {/* Infinite scroll trigger */}
              {!query && galleryHasMore && (
                <div ref={loadMoreTriggerRef} className="flex justify-center py-4">
                  {galleryLoadingMore ? (
                    <div className="flex items-center gap-2 text-gray-500">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
                      <span className="text-sm">Loading more...</span>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">Scroll for more</span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
